import React, { useState, useEffect, useCallback } from 'react';
import { Navigate, useParams } from "react-router-dom";

import { Button, Divider, Tab, Tabs } from "@mui/material";
import Grid from '@mui/material/Grid2';
import { 
  ModelBase,
} from "@models";
import { 
  SBSection, 
  SourceBook, 
} from "@dndModels";
import { 
  SOURCE_BOOK_TYPES 
} from '@dndModels/Constants';
import { 
  SourceBookType 
} from '@dndModels/Types';

import API from "@dndUtilities/system_api";
import CommonUtilities from "@utilities/common_utilities";

import LoadingBox from "@components/input/LoadingBox";
import ButtonBox from "@components/input/buttons/ButtonBox";
import StringBox from "@components/input/strings/StringBox";

import ModelBaseInput from "@dndComponents/ModelBaseInput";
import CheckBox from "@components/input/CheckBox";
import SBSectionsEditor from "@dndComponents/SBSectionsEditor";
import SBSectionsViewer from "@dndComponents/SBSectionsViewer";
import PdfImporter from '@components/input/Importer/PdfImporter';
import ArrowBackButton from '@components/input/buttons/icon_buttons/ArrowBackButton';
import SelectStringBox from '@components/input/selects/SelectStringBox';

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();

interface Props {
  obj?: ModelBase;
}

export default function SourceBookEdit({ obj: param_obj }: Props) {
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [navigateTo, setNavigateTo] = useState<string | null>(null);
  const [obj, setObj] = useState<SourceBook>(param_obj instanceof SourceBook ? param_obj : api.applyWorkingOn(new SourceBook()));
  const [change_time, setChanged] = useState(-1);
  const [tab, setTab] = useState(0);
  const [topHeight, setTopHeight] = useState(0);

  const disabled = api.user === null || !api.online || (!api.user.admin && obj._id !== "" && obj.owner_id !== api.user._id);

  // Read-only view for logged-in non-admin users with access
  const user = api.user;
  const canViewContent = user !== null &&
    !user.admin &&
    (obj.is_free || user.purchased_sb_ids.includes(obj._id) || user.shared_sb_ids.includes(obj._id));

  const topRef = useCallback((el: HTMLDivElement | null) => {
    if (el) {
      // debugger;
      setTopHeight(el.getBoundingClientRect().height);
    }
  }, [disabled, canViewContent]); // deps that change top bar height

  const loadSpecialSections = useCallback( async () => {
    const disabled2 = api.user === null || !api.online || (!api.user.admin && obj._id !== "" && obj.owner_id !== api.user._id);
  
    const canViewContent2 = api.user !== null &&
      !api.user.admin &&
      (obj.is_free || api.user.purchased_sb_ids.includes(obj._id) || api.user.shared_sb_ids.includes(obj._id));
    if (disabled2 && canViewContent2) {
      // Check for pseudo sections
      const findSpecialSections = (section: SBSection): SBSection[] => {
        if (section.special_type !== "None") {
          return [section];
        } else {
          return section.subsections.flatMap(s => findSpecialSections(s));
        }
      }
      // debugger;
      const special_sections = obj.sections.flatMap(s => findSpecialSections(s));
      for (const section of special_sections) {
        // debugger;
        section.subsections = await api.getSubsectionsForSpecialSection(section, obj);
      }
      console.log(special_sections);
      console.log(obj);
    }
    setLoading(false);
  }, []);

  const load = useCallback( async () => {
    await api.loadObject(obj);
    loadSpecialSections();
  }, [obj, loadSpecialSections]);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      setLoading(true);
      setTimeout(() => {
        load();
      }, 100);
    }
  }, [fetched, load]);

  const renderPurchase = () => {
    if (obj.is_free) {
      return null;
    } else {
      const user = api.user;
      if (user) {
        if (user.purchased_sb_ids.includes(obj._id)) {
          return (
            <Grid container spacing={0} direction="row">
              <Grid size={{ xs: 6 }}>
                You have purchased this source book
              </Grid>
              <Grid size={{ xs: 6 }}>
                <ButtonBox 
                  name="Unpurchase this Source Book"
                  disabled={processing || !api.online}
                  onClick={() => {
                    const user = api.user;
                    if (user) {
                      setProcessing(true);
                      user.purchased_sb_ids = user.purchased_sb_ids.filter(o => o !== obj._id);
                      api.updateObject("user", user).then(res => {
                        setProcessing(false);
                      });
                    }
                  }}
                />
              </Grid>
            </Grid>
          );
        } else if (user.shared_sb_ids.includes(obj._id)) {
          return [
            <Grid key="message">
              This source book has been shared with you, but you can still purchase it
            </Grid>,
            renderPurchaseButton()
          ];
        } else {
          return renderPurchaseButton();
        }
      } else {
        return (
          <Grid>You need to log in to be able to purchase a source book.</Grid>
        );
      }
    }
  }

  const renderPurchaseButton = () => {
    return (
      <Grid key="purchase">
        <ButtonBox 
          name="Purchase this Source Book"
          disabled={processing || !api.online}
          onClick={() => {
            const user = api.user;
            if (user) {
              setProcessing(true);
              user.purchased_sb_ids.push(obj._id);
              api.updateObject("user", user).then(res => {
                setProcessing(false);
              });
            }
          }}
        />(Currently just sets it to purchased in the db. There are no charges.)
      </Grid>
    );
  }

  if (!fetched || loading) {
    const height = window.innerHeight - 64;
    return (<LoadingBox size={height * 0.5} height={height} width={window.innerWidth} />);
  } else if (navigateTo !== null) {
    return <Navigate to={navigateTo} />;
  }

  const renderMain = () => (
    <ModelBaseInput
      obj={obj}
      disabled={disabled}
      change_time={change_time}
      no_source
      no_scrollbar
      onChange={(changed: ModelBase) => {
        if (changed instanceof SourceBook) {
          const new_obj = obj;
          new_obj.copy(changed);
          setObj(new_obj);
        }
      }}>
      <Grid container spacing={0} direction="row">
        <Grid size={{ xs: 12 }}>
          { disabled
            ? <><span className="header">Abbreviation:</span> {obj.abbreviation}</>
            : <StringBox 
                name="Abbreviation"
                value={obj.abbreviation}
                onBlur={(changed: string) => {
                  const new_obj = obj;
                  new_obj.abbreviation = changed;
                  setObj(new_obj);
                  setChanged(common_util.get_time_number());
                }}
              />
          }
        </Grid>
        <Grid size={6}>
          { disabled
            ? <><span className="header">Type:</span> { obj.source_book_type }</>
          : 
            <SelectStringBox 
              name="Type"
              value={obj.source_book_type}
              options={SOURCE_BOOK_TYPES}
              onChange={(changed: SourceBookType) => {
                const new_obj = obj;
                new_obj.source_book_type = changed;
                setObj(new_obj);
                setChanged(common_util.get_time_number());
              }}
            />
          }
        </Grid>
        <Grid size={6}>
          { disabled
            ? <>{ obj.is_free ? `This Source Book is free` : "" }</>
          : 
            <CheckBox 
              name="Free"
              value={obj.is_free}
              onChange={(changed: boolean) => {
                const new_obj = obj;
                new_obj.is_free = changed;
                setObj(new_obj);
                setChanged(common_util.get_time_number());
              }}
            />
          }
        </Grid>
        <Grid size={12}>
          { disabled
            ? <>{ obj.is_free ? `This Source Book is free` : "" }</>
          : 
            <CheckBox 
              name="Free"
              value={obj.is_free}
              onChange={(changed: boolean) => {
                const new_obj = obj;
                new_obj.is_free = changed;
                setObj(new_obj);
                setChanged(common_util.get_time_number());
              }}
            />
          }
        </Grid>
        { disabled && !canViewContent &&
          <Grid className="padded" size={{ xs: 12 }}>
            { renderPurchase() }
          </Grid>
        }
        { disabled ? 
          <>
            { obj.third_party && (
              <Grid className="header" size={12}>Third Party</Grid>
            )}
          </>
        : <Grid size={ api.user && api.user.admin ? 6 : 12 }>
            <CheckBox 
              name="Third Party"
              value={obj.third_party}
              onChange={(changed: boolean) => {
                const new_obj = obj;
                new_obj.third_party = changed;
                setObj(new_obj);
                setChanged(common_util.get_time_number());
              }}
            />
          </Grid>
        }
        { api.user && api.user.admin && 
          <Grid size={6}>
            <CheckBox 
              name="Public"
              value={obj.is_public}
              onChange={(changed: boolean) => {
                const new_obj = obj;
                new_obj.is_public = changed;
                setObj(new_obj);
                setChanged(common_util.get_time_number());
              }}
            />
          </Grid>
        }
      </Grid>
    </ModelBaseInput>
  );

  // Non-admin editors get the tabbed layout
  if (!disabled) {
    return (
      <>
        <Tabs value={tab} onChange={(_, val) => setTab(val)} style={{ marginBottom: 16 }}>
          <Tab label="Main" />
          <Tab label="Content" />
          { api.true_user && api.true_user.admin && <Tab label="Import PDF" /> }
        </Tabs>

        { tab === 0 && renderMain() }

        { tab === 1 && (
          <SBSectionsEditor
            source_book={obj}
            sections={obj.sections}
          />
        )}

        { tab === 2 && (
          <PdfImporter
            onExport={async (sections) => {
              const new_obj = obj;
              const sbSectionsMaxPos = Math.max(...new_obj.sections.map(o => o.position), -1);
              const handleSection = async (section: SBSection) => {
                const res = await api.upsertObject("sb_section", section);
                
                if (res) {
                  section._id = res.body.id;
                  if (section.subsections.length > 0) {
                    for (let i = 0; i < section.subsections.length; ++i) {
                      const subsection = section.subsections[i];
                      subsection.parent_id = section._id;
                      subsection.parent_type = "SBSection";
                      subsection.position = i;
                      await handleSection(subsection);
                    }
                    await api.upsertObject("sb_section", section);
                  }
                }
              }
              for (let i = 0; i < sections.length; ++i) {
                const section = sections[i];
                section.parent_id = new_obj._id;
                section.parent_type = "Source Book";
                section.position = sbSectionsMaxPos + i;
                await handleSection(section);
              }
              new_obj.sections = [...new_obj.sections, ...sections];
              await api.upsertObject("source_book", new_obj);
              setObj(new_obj);
              setChanged(common_util.get_time_number());
              setTab(1);
            }}
          />
        )}
      </>
    );
  }

  if (disabled && canViewContent) {
    return (
      <Grid container spacing={0} direction="row">
        <Grid size={12} ref={topRef}>
          <Grid container spacing={0} direction="row">
            <Grid size={12}>
              <ArrowBackButton
                onClick={ () => {
                  setNavigateTo(`/${obj.data_type}`);
                }}
              />
            </Grid>
            <Grid size={12}>
              <span className="header">Name:</span> { obj.name }
            </Grid>
            <Grid size={12}>
              <span className="header">Abbreviation:</span> { obj.abbreviation }
            </Grid>
            <Grid size={6}>
              <span className="header">Type:</span> { obj.source_book_type }
            </Grid>
            <Grid size={6}>
              { obj.is_free && 
                "This Source Book is free"
              }
            </Grid>
            <Grid size={12}>
              <Divider style={{ margin: '24px 0 16px' }} />
            </Grid>
          </Grid>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <div style={{ width: "calc(100vw - 17px)" }}>
            <SBSectionsViewer
              source_book={obj}
              sections={obj.sections}
              topHeight={topHeight}
            />
          </div>
        </Grid>
      </Grid>
    );
  } else {
    return (
      <>
        { renderMain() }
      </>
    );
  }
}